import { useState, useRef, useMemo } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Step,
    StepConnector,
    stepConnectorClasses,
    StepLabel,
    Stepper,
    FormControlLabel,
    FormControl,
    FormLabel,
    Radio,
    RadioGroup,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';

import { useCustomAuth } from 'auth/AuthContext';
import AssessmentDropdown from 'domains/content/components/Bee/AssessmentDialog/AssessmentDropdown';
import ConditionsBuilderDialog from 'domains/content/components/Bee/ConditionsBuilderDialog';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import InputLabel from 'domains/core/components/InputLabel';
import Select from 'domains/core/components/Select';
import useExecuteInconnectImport from 'hooks/mutations/useExecuteInconnectImport';
import useExecuteMigrateTemplate, {
    ConditionMappingItem,
    ConversionMode,
} from 'hooks/mutations/useExecuteMigrateTemplate';
import useParseMigrateTemplate, {
    ConditionExpressionItem,
    MigrationSource,
} from 'hooks/mutations/useParseMigrateTemplate';
import useSaveImportMappings from 'hooks/mutations/useSaveImportMappings';
import useAssessments, { Assessment } from 'hooks/queries/useAssessments';
import useContactColumns from 'hooks/queries/useContactColumns';
import useRowDisplayConditions, { RowDisplayCondition } from 'hooks/queries/useDisplayConditions';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import useInconnectTemplates from 'hooks/queries/useInconnectTemplates';
import useDebounce from 'hooks/useDebounce';
import useGetAxiosInstance from 'hooks/useGetAxiosInstance';
import { ApiMethods, URLPaths } from 'models/enums';
import { InconnectTemplate } from 'models/types';

// ─── Types ───────────────────────────────────────────────────────────────────

type ImportSource = MigrationSource | 'inconnect';

// ─── Constants ───────────────────────────────────────────────────────────────

const SALESFORCE_STEPS = ['Upload', 'Match Tags', 'Display Conditions', 'File Details', 'Review'];

const INCONNECT_STEPS_BASE = ['Source & Template', 'Map Tags', 'Review & Import'];
const INCONNECT_STEPS_WITH_ASSESSMENT = ['Source & Template', 'Map Tags', 'Pick Assessment', 'Review & Import'];
// System tags present in Inconnect HTML — excluded from user tag mapping.
// UNSUBSCRIBE_URL is auto-replaced; assessment_url (and its common typo) requires the assessment picker step.
const INCONNECT_SYSTEM_TAGS = new Set(['UNSUBSCRIBE_URL', 'assessment_url', 'assesssment_url']);
// Languages supported by Cured's multilingual system (Inconnect code → Cured code)
const INCONNECT_SUPPORTED_LANGUAGES: Record<string, string> = { 'en-us': 'en-US', 'es-cr': 'es-CR' };

const SOURCE_LABELS: Record<ImportSource, string> = {
    salesforce: 'Salesforce Marketing Cloud',
    inconnect: 'Inconnect',
};

// Set to true to re-enable suggested condition matching in the Display Conditions step
const SHOW_SUGGESTED_CONDITIONS = false;

/**
 * Suggests an existing display condition for the given expression.
 *
 * Strategy: extract the SFMC field name from the expression, map it to the
 * Cured field via conditionFieldMapping, then find conditions whose label or
 * description contains that exact Cured field name. This avoids the false
 * positives that arise from fuzzy token matching on expression values.
 *
 * Returns null for ELSE blocks (the negation can't be inferred reliably) and
 * when the field has not yet been mapped by the user.
 */
const getSuggestedCondition = (
    expr: ConditionExpressionItem,
    existingConditions: RowDisplayCondition[],
    conditionFieldMapping: Record<string, string>
): RowDisplayCondition | null => {
    if (!existingConditions.length || expr.isElse) return null;

    const fieldMatch = expr.expression?.match(/\[(\w+)\]/);
    if (!fieldMatch) return null;

    const curedField = conditionFieldMapping[fieldMatch[1]];
    if (!curedField) return null;

    const fieldLower = curedField.toLowerCase();
    const fieldSpaced = fieldLower.replace(/_/g, ' ');

    const matches = existingConditions.filter((cond) => {
        const haystack = `${cond.label} ${cond.description}`.toLowerCase();
        return haystack.includes(fieldLower) || haystack.includes(fieldSpaced);
    });

    return matches[0] ?? null;
};

// ─── Component ───────────────────────────────────────────────────────────────

const ImportTemplatePage = () => {
    const history = useHistory();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [activeStep, setActiveStep] = useState(0);
    const [htmlContent, setHtmlContent] = useState('');
    const [fileName, setFileName] = useState('');
    const [parseError, setParseError] = useState('');

    // Step 0: source + import configuration
    const [source, setSource] = useState<ImportSource>('salesforce');
    const [conversionMode, setConversionMode] = useState<ConversionMode>('native');

    // Salesforce — step 1: tag mapping
    const [tagMapping, setTagMapping] = useState<Record<string, string>>({});
    const [conditionFieldMapping, setConditionFieldMapping] = useState<Record<string, string>>({});

    // Salesforce — step 2: condition mapping
    const [conditionMapping, setConditionMapping] = useState<Record<string, ConditionMappingItem>>({});
    // Tracks which condition expression has the create-condition dialog open
    const [conditionDialogExprId, setConditionDialogExprId] = useState<string | null>(null);

    // Inconnect — template browser state
    // Selections store the full template object (all language variants imported together)
    const [selectedInconnectTemplates, setSelectedInconnectTemplates] = useState<InconnectTemplate[]>([]);
    const [inconnectSearch, setInconnectSearch] = useState('');
    const debouncedSearch = useDebounce(inconnectSearch, 300);
    const isSearchMode = debouncedSearch.trim().length > 0;
    // Pagination: stack of previously-visited offsets so Prev works correctly
    const [inconnectCurrentOffset, setInconnectCurrentOffset] = useState<number | undefined>(undefined);
    const [inconnectPrevOffsets, setInconnectPrevOffsets] = useState<(number | undefined)[]>([]);
    // Status filter: '' = all, 'PUBLISHED' | 'DRAFT' = filtered
    const [inconnectStatusFilter, setInconnectStatusFilter] = useState<string>('');
    // Full-content templates fetched individually (list API truncates message HTML)
    const [inconnectEnrichedTemplates, setInconnectEnrichedTemplates] = useState<InconnectTemplate[]>([]);
    const [isFetchingFullContent, setIsFetchingFullContent] = useState(false);
    // Assessment selection — per-template, shown when {{assessment_url}} is detected in selected templates
    const [templateAssessments, setTemplateAssessments] = useState<
        Record<number, AutocompleteOption<Assessment> | null>
    >({});
    const [templateConsentRequired, setTemplateConsentRequired] = useState<Record<number, boolean>>({});
    const [templateAssessmentSearchInput, setTemplateAssessmentSearchInput] = useState<Record<number, string>>({});

    // Shared — step 3/4: template details + error
    const [templateName, setTemplateName] = useState('');
    const [subjectLine, setSubjectLine] = useState('');
    const [preHeader, setPreHeader] = useState('');
    const [executeError, setExecuteError] = useState('');

    // ─── Data queries ─────────────────────────────────────────────────────────

    const { data: contactColumnsData } = useContactColumns();
    const mergeTagColumns = (contactColumnsData ?? []).filter((col) => col.isMergeTag);

    const { data: existingConditions = [] } = useRowDisplayConditions();

    const { data: featureSettings } = useFeatureSettings();
    const isPlatformIntegrated = !!featureSettings?.platformIntegrated;
    const { isInnovaccerLogin } = useCustomAuth();
    const getApiInstance = useGetAxiosInstance(ApiMethods.GET);

    const { data: inconnectTemplatesData, isLoading: isInconnectTemplatesLoading } = useInconnectTemplates(
        isSearchMode ? { name: debouncedSearch } : { offset: inconnectCurrentOffset },
        { enabled: source === 'inconnect' }
    );

    // In browse mode the API returns page.next; in search mode there is no paging.
    const inconnectNextOffset = isSearchMode ? undefined : inconnectTemplatesData?.page?.next?.offset;
    const currentPageTemplates: InconnectTemplate[] = inconnectTemplatesData?.data ?? [];
    const filteredPageTemplates = inconnectStatusFilter
        ? currentPageTemplates.filter((t) => t.state === inconnectStatusFilter)
        : currentPageTemplates;

    // ─── Mutations ────────────────────────────────────────────────────────────

    const { mutate: parseMigrateTemplate, isLoading: isParsing, data: parseResult } = useParseMigrateTemplate({
        onSuccess: (data) => {
            if (data.savedTagMappings) {
                setTagMapping((prev) => ({ ...data.savedTagMappings, ...prev }));
            }
            if (data.savedConditionFieldMappings) {
                setConditionFieldMapping((prev) => ({ ...data.savedConditionFieldMappings, ...prev }));
            }
        },
    });

    const { mutate: saveImportMappings } = useSaveImportMappings();

    const { mutate: executeMigrateTemplate, isLoading: isMigrateExecuting } = useExecuteMigrateTemplate({
        onSuccess: () => history.push(URLPaths.CONTENT),
        onError: (err) => setExecuteError(err.message ?? 'Something went wrong. Please try again.'),
    });

    const { mutateAsync: executeInconnectImportAsync, isLoading: isInconnectExecuting } = useExecuteInconnectImport();

    const isExecuting = isMigrateExecuting || isInconnectExecuting;

    // ─── Derived: source options ───────────────────────────────────────────────

    const sourceOptions = [
        { label: 'Salesforce Marketing Cloud', value: 'salesforce' as ImportSource },
        ...(isPlatformIntegrated && isInnovaccerLogin
            ? [{ label: 'Inconnect', value: 'inconnect' as ImportSource }]
            : []),
    ];

    // Returns true if a single template contains the assessment_url tag (handles common triple-s typo)
    const templateHasAssessmentTag = (t: InconnectTemplate) =>
        t.template.some((v) => /\{\{assesss?ment_url\}\}/i.test(v.message ?? ''));

    // Detect {{assessment_url}} (or typo {{assesssment_url}}) in any enriched template variant — triggers assessment picker step
    const hasAssessmentTag = useMemo(() => inconnectEnrichedTemplates.some(templateHasAssessmentTag), [
        inconnectEnrichedTemplates,
    ]);

    const inconnectSteps = hasAssessmentTag ? INCONNECT_STEPS_WITH_ASSESSMENT : INCONNECT_STEPS_BASE;
    const steps = source === 'inconnect' ? inconnectSteps : SALESFORCE_STEPS;

    // Pre-fetch assessments as soon as we know the selected templates contain {{assessment_url}}.
    // This starts loading in the background while the user is still on the tag-mapping step,
    // so the assessment picker step appears instantly. isAssessmentsLoading drives the step loader.
    const { isLoading: isAssessmentsLoading } = useAssessments({
        reactQueryOptions: { enabled: source === 'inconnect' && hasAssessmentTag },
    });

    // ─── Derived: Salesforce parse results ────────────────────────────────────

    const conditionExpressions: ConditionExpressionItem[] = parseResult?.conditionExpressions ?? [];
    const personalizationTags: string[] = parseResult?.personalizationTags ?? [];
    const systemTagsMapped: string[] = parseResult?.systemTagsMapped ?? [];
    const systemTagsRemoved: string[] = parseResult?.systemTagsRemoved ?? [];

    const allTagsMapped = personalizationTags.every((tag) => !!tagMapping[tag]);
    const allConditionsMapped = conditionExpressions.every((expr) => !!conditionMapping[expr.id]);

    // ─── Derived: Inconnect tags ──────────────────────────────────────────────

    // Collect user-mappable tags across ALL language variants of all selected templates.
    // Uses enriched (full-HTML) templates once they've been fetched; falls back to list data.
    const inconnectPersonalizationTags = useMemo(() => {
        const source = inconnectEnrichedTemplates.length > 0 ? inconnectEnrichedTemplates : selectedInconnectTemplates;
        const tagSet = new Set<string>();
        for (const template of source) {
            for (const variant of template.template) {
                Array.from((variant.message ?? '').matchAll(/\{\{(\w+)\}\}/g))
                    .filter((m) => !INCONNECT_SYSTEM_TAGS.has(m[1]))
                    .forEach((m) => tagSet.add(m[1]));
            }
        }
        return Array.from(tagSet);
    }, [inconnectEnrichedTemplates, selectedInconnectTemplates]);

    const allInconnectTagsMapped = inconnectPersonalizationTags.every((tag) => !!tagMapping[tag]);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const triggerParse = (content: string, parseSource: MigrationSource) => {
        setParseError('');
        parseMigrateTemplate(
            { htmlContent: content, source: parseSource },
            { onError: (err) => setParseError(err.message ?? 'Failed to parse HTML.') }
        );
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setParseError('');
        const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
        setTemplateName((prev) => (prev.trim() ? prev : baseName));
        const reader = new FileReader();
        reader.onload = (ev) => {
            const buffer = ev.target?.result as ArrayBuffer;
            let content: string;
            try {
                content = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
            } catch {
                content = new TextDecoder('windows-1252').decode(buffer);
            }
            content = content.replace(/^\uFEFF/, '');
            setHtmlContent(content);
            triggerParse(content, 'salesforce');
        };
        reader.readAsArrayBuffer(file);
    };

    const handleSourceChange = (newSource: ImportSource) => {
        if (newSource === source) return;
        setSource(newSource);
        setActiveStep(0);
        // Reset source-specific state
        setSelectedInconnectTemplates([]);
        setInconnectSearch('');
        setInconnectCurrentOffset(undefined);
        setInconnectPrevOffsets([]);
        setTagMapping({});
        setConditionFieldMapping({});
        if (newSource === 'salesforce' && htmlContent) {
            triggerParse(htmlContent, 'salesforce');
        }
    };

    const handleNext = async () => {
        // Persist Salesforce tag + condition field mappings when leaving step 1
        if (activeStep === 1 && source === 'salesforce') {
            saveImportMappings({ source: 'salesforce', tagMapping, conditionFieldMapping });
        }

        // Fetch full HTML for each selected Inconnect template before entering the tag step.
        // The list API only returns ~200 chars of message; full content is needed for tag extraction.
        // Also fetch saved Inconnect tag mappings so the mapping step is pre-populated.
        if (activeStep === 0 && source === 'inconnect') {
            setIsFetchingFullContent(true);
            try {
                const api = await getApiInstance();
                const [enriched, savedMappingsRes] = await Promise.all([
                    Promise.all(
                        selectedInconnectTemplates.map(async (t) => {
                            const res = await api.get(`inconnect/templates/${t.id}`);
                            const detail = res.data;
                            // Merge full-HTML variants back onto the list template (preserves language from list)
                            const enrichedVariants = t.template.map((listVariant) => {
                                const fullVariant = detail.template?.find((dv: any) => dv.id === listVariant.id);
                                return fullVariant ? { ...listVariant, message: fullVariant.message } : listVariant;
                            });
                            return { ...t, template: enrichedVariants };
                        })
                    ),
                    api.get('templates/migrate/mappings?source=inconnect').catch(() => null),
                ]);
                setInconnectEnrichedTemplates(enriched);
                // Pre-populate tag mapping from previously saved Inconnect mappings.
                // User's existing selections (prev) take precedence over saved defaults.
                const savedMappings: Record<string, string> = savedMappingsRes?.data?.tagMapping ?? {};
                if (Object.keys(savedMappings).length > 0) {
                    setTagMapping((prev) => ({ ...savedMappings, ...prev }));
                }
            } catch {
                // Non-fatal: tag mapping step will show whatever was extracted
            } finally {
                setIsFetchingFullContent(false);
            }
        }

        setActiveStep((s) => s + 1);
    };

    const handleBack = () => setActiveStep((s) => s - 1);

    const handleExecute = async () => {
        setExecuteError('');
        if (source === 'inconnect') {
            try {
                const templatesToImport =
                    inconnectEnrichedTemplates.length > 0 ? inconnectEnrichedTemplates : selectedInconnectTemplates;
                for (const template of templatesToImport) {
                    // Only send supported language variants (en-us, es-cr) with HTML content
                    const variants = template.template
                        .filter((v) => !!v.message && v.language?.toLowerCase() in INCONNECT_SUPPORTED_LANGUAGES)
                        .map((v) => ({
                            language: v.language.toLowerCase(),
                            htmlContent: v.message,
                            subjectLine: v.subject || undefined,
                        }));
                    if (variants.length === 0) continue;
                    const thisTemplateAssessment = templateHasAssessmentTag(template)
                        ? templateAssessments[template.id]
                        : undefined;
                    await executeInconnectImportAsync({
                        inconnectTemplateId: template.id,
                        tagMapping,
                        templateName: template.name,
                        variants,
                        conversionMode,
                        assessmentId: thisTemplateAssessment?.value.id,
                        assessmentName: thisTemplateAssessment?.value.name,
                        assessmentConsentRequired: thisTemplateAssessment
                            ? templateConsentRequired[template.id] ?? true
                            : undefined,
                    });
                }
                // Persist Inconnect tag mappings so next import pre-populates them
                saveImportMappings({ source: 'inconnect', tagMapping, conditionFieldMapping: {} });
                history.push(URLPaths.CONTENT);
            } catch (err) {
                setExecuteError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
            }
            return;
        }
        // Salesforce path
        executeMigrateTemplate({
            htmlContent,
            templateName,
            subjectLine: subjectLine || undefined,
            preHeader: preHeader || undefined,
            source: 'salesforce',
            conversionMode,
            tagMapping,
            conditionFieldMapping,
            conditionMapping: Object.values(conditionMapping),
        });
    };

    // ─── canProceedFromStep ────────────────────────────────────────────────────

    const canProceedFromStep = () => {
        if (source === 'inconnect') {
            switch (activeStep) {
                case 0:
                    return selectedInconnectTemplates.length > 0;
                case 1:
                    return allInconnectTagsMapped || inconnectPersonalizationTags.length === 0;
                case 2:
                    // When assessment step is present, every template with the tag must have an assessment selected
                    return hasAssessmentTag
                        ? inconnectEnrichedTemplates
                              .filter(templateHasAssessmentTag)
                              .every((t) => !!templateAssessments[t.id])
                        : true;
                default:
                    return true;
            }
        }
        switch (activeStep) {
            case 0:
                return !!parseResult && !isParsing && !parseError;
            case 1:
                return allTagsMapped || personalizationTags.length === 0;
            case 2:
                return allConditionsMapped || conditionExpressions.length === 0;
            case 3:
                return !!templateName.trim();
            default:
                return true;
        }
    };

    // ─── Render: shared template-details form ─────────────────────────────────

    const renderTemplateDetails = () => (
        <Stack spacing={3} sx={{ maxWidth: 480 }}>
            <Typography variant="body1" color="text.secondary">
                Provide the details for the imported template.
            </Typography>
            <TextField
                label="Template name"
                required
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={source === 'inconnect' ? 'e.g. Welcome Email' : 'e.g. SFMC Welcome Email'}
            />
            <TextField
                label="Subject line"
                value={subjectLine}
                onChange={(e) => setSubjectLine(e.target.value)}
                placeholder="e.g. Welcome to our program!"
            />
            <TextField
                label="Pre-header"
                value={preHeader}
                onChange={(e) => setPreHeader(e.target.value)}
                placeholder="e.g. Here's what you need to know..."
            />
        </Stack>
    );

    // ─── Render: step 0 — source selection + source-specific content ──────────

    const renderStep1 = () => (
        <Stack spacing={3} alignItems="flex-start">
            <Select
                label={
                    <InputLabel
                        label="Source Platform"
                        labelIconTooltipText="The platform your HTML template was created in."
                    />
                }
                value={source}
                onChange={(e) => handleSourceChange(e.target.value as ImportSource)}
                values={sourceOptions}
                sx={{ width: 320 }}
                data-testid="source-select"
            />

            {/* ── Salesforce-specific: conversion mode + file upload ── */}
            {source === 'salesforce' && (
                <Stack spacing={3} alignItems="flex-start" width="100%">
                    <FormControl>
                        <FormLabel sx={{ mb: 1, fontWeight: 500, color: 'text.primary', fontFamily: 'inherit' }}>
                            Conversion Strategy
                        </FormLabel>
                        <RadioGroup
                            value={conversionMode}
                            onChange={(e) => setConversionMode(e.target.value as ConversionMode)}
                            data-testid="conversion-mode-select"
                            sx={{ gap: 1.5 }}
                        >
                            <FormControlLabel
                                value="native"
                                control={<Radio sx={{ alignSelf: 'flex-start', mt: 0.5 }} />}
                                label={
                                    <Stack spacing={0}>
                                        <Typography variant="body2">As native BEE JSON</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            All modules editable (images, text, buttons, dividers), hampers layout
                                        </Typography>
                                    </Stack>
                                }
                            />
                            <FormControlLabel
                                value="html"
                                control={<Radio sx={{ alignSelf: 'flex-start', mt: 0.5 }} />}
                                label={
                                    <Stack spacing={0}>
                                        <Typography variant="body2">As RAW HTML</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Layout preserved, less editable in native builder
                                        </Typography>
                                    </Stack>
                                }
                            />
                        </RadioGroup>
                    </FormControl>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            variant="outlined"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isParsing}
                            data-testid="choose-file-button"
                        >
                            {fileName ? 'Replace file' : 'Choose HTML file'}
                        </Button>
                        {fileName && (
                            <Typography variant="body2" color="text.secondary">
                                Selected: <strong>{fileName}</strong>
                            </Typography>
                        )}
                    </Stack>
                    <input ref={fileInputRef} type="file" accept=".html,.htm" hidden onChange={handleFileChange} />
                    {isParsing && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={16} role="progressbar" aria-label="Parsing HTML" />
                            <Typography variant="body2">Parsing HTML...</Typography>
                        </Stack>
                    )}
                    {parseError && <Alert severity="error">{parseError}</Alert>}
                    {parseResult && !isParsing && (
                        <Alert severity="success">
                            Found {personalizationTags.length} personalization tag
                            {personalizationTags.length !== 1 ? 's' : ''} and {conditionExpressions.length} condition
                            expression{conditionExpressions.length !== 1 ? 's' : ''}.
                        </Alert>
                    )}
                </Stack>
            )}

            {/* ── Inconnect-specific: template browser ── */}
            {source === 'inconnect' && (
                <Stack spacing={2} width="100%">
                    <FormControl>
                        <FormLabel sx={{ mb: 1, fontWeight: 500, color: 'text.primary', fontFamily: 'inherit' }}>
                            Conversion Strategy
                        </FormLabel>
                        <RadioGroup
                            value={conversionMode}
                            onChange={(e) => setConversionMode(e.target.value as ConversionMode)}
                            data-testid="inconnect-conversion-mode-select"
                        >
                            <FormControlLabel
                                value="native"
                                control={<Radio sx={{ alignSelf: 'flex-start', mt: 0.5 }} />}
                                label={
                                    <Stack spacing={0}>
                                        <Typography variant="body2">As native BEE JSON</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            All modules editable (images, text, buttons, dividers), hampers layout
                                        </Typography>
                                    </Stack>
                                }
                            />
                            <FormControlLabel
                                value="html"
                                control={<Radio sx={{ alignSelf: 'flex-start', mt: 0.5 }} />}
                                label={
                                    <Stack spacing={0}>
                                        <Typography variant="body2">As RAW HTML</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Layout preserved, less editable in native builder
                                        </Typography>
                                    </Stack>
                                }
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Toolbar: search + status filter + selection count */}
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <TextField
                            label="Search templates"
                            size="small"
                            value={inconnectSearch}
                            onChange={(e) => {
                                setInconnectSearch(e.target.value);
                                setInconnectCurrentOffset(undefined);
                                setInconnectPrevOffsets([]);
                            }}
                            sx={{ width: 280 }}
                            data-testid="inconnect-search"
                        />
                        <ToggleButtonGroup
                            value={inconnectStatusFilter}
                            exclusive
                            size="small"
                            onChange={(_, v) => setInconnectStatusFilter(v ?? '')}
                            aria-label="Filter by status"
                        >
                            <ToggleButton value="" aria-label="All statuses">
                                All
                            </ToggleButton>
                            <ToggleButton value="PUBLISHED" aria-label="Published">
                                Published
                            </ToggleButton>
                            <ToggleButton value="DRAFT" aria-label="Draft">
                                Draft
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {selectedInconnectTemplates.length > 0 && (
                            <Typography variant="body2" color="primary.main" fontWeight={500}>
                                {selectedInconnectTemplates.length} selected
                            </Typography>
                        )}
                    </Stack>

                    {/* Template table */}
                    {isInconnectTemplatesLoading ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                            <CircularProgress size={16} role="progressbar" aria-label="Loading Inconnect templates" />
                            <Typography variant="body2">
                                {isSearchMode ? 'Searching...' : 'Loading templates...'}
                            </Typography>
                        </Stack>
                    ) : filteredPageTemplates.length === 0 ? (
                        <Alert severity="info">
                            {inconnectStatusFilter && currentPageTemplates.length > 0
                                ? `No ${inconnectStatusFilter.toLowerCase()} templates on this page.`
                                : isSearchMode
                                ? 'No templates match your search.'
                                : 'No templates found.'}
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>Name</TableCell>
                                        <TableCell sx={{ width: 110 }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredPageTemplates.map((template) => {
                                        const isSelected = selectedInconnectTemplates.some((t) => t.id === template.id);
                                        const toggleSelection = () => {
                                            setSelectedInconnectTemplates((prev) =>
                                                prev.some((t) => t.id === template.id)
                                                    ? prev.filter((t) => t.id !== template.id)
                                                    : [...prev, template]
                                            );
                                        };
                                        return (
                                            <TableRow
                                                key={template.id}
                                                hover
                                                onClick={toggleSelection}
                                                sx={{
                                                    cursor: 'pointer',
                                                    bgcolor: isSelected ? 'action.hover' : undefined,
                                                }}
                                                data-testid={`inconnect-template-${template.id}`}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={isSelected}
                                                        size="small"
                                                        inputProps={{ 'aria-label': `Select ${template.name}` }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={toggleSelection}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{template.name}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {template.state && (
                                                        <Typography
                                                            variant="caption"
                                                            color={
                                                                template.state === 'PUBLISHED'
                                                                    ? 'success.main'
                                                                    : 'text.secondary'
                                                            }
                                                            fontWeight={500}
                                                        >
                                                            {template.state}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Pagination footer — only in browse mode */}
                    {!isSearchMode && !isInconnectTemplatesLoading && currentPageTemplates.length > 0 && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                variant="text"
                                size="small"
                                aria-label="Previous page"
                                disabled={inconnectPrevOffsets.length === 0}
                                onClick={() => {
                                    const prev = inconnectPrevOffsets[inconnectPrevOffsets.length - 1];
                                    setInconnectCurrentOffset(prev);
                                    setInconnectPrevOffsets((ps) => ps.slice(0, -1));
                                }}
                                data-testid="inconnect-prev-page"
                            >
                                Previous
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                                Page {inconnectPrevOffsets.length + 1}
                                {inconnectTemplatesData?.count
                                    ? ` of ${Math.ceil(inconnectTemplatesData.count / 10)}`
                                    : ''}
                            </Typography>
                            <Button
                                variant="text"
                                size="small"
                                aria-label="Next page"
                                disabled={inconnectNextOffset === undefined}
                                onClick={() => {
                                    setInconnectPrevOffsets((ps) => [...ps, inconnectCurrentOffset]);
                                    setInconnectCurrentOffset(inconnectNextOffset);
                                }}
                                data-testid="inconnect-next-page"
                            >
                                Next
                            </Button>
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    );

    // ─── Render: Salesforce step 1 — tag + condition field mapping ────────────

    const renderStep2 = () => {
        const hasAnything =
            personalizationTags.length > 0 || systemTagsMapped.length > 0 || systemTagsRemoved.length > 0;

        return (
            <Stack spacing={3}>
                {!hasAnything && <Alert severity="info">No fields to map in this template.</Alert>}

                {/* Personalization tag mapping — only shown when there are user-mappable tags */}
                {personalizationTags.length > 0 && (
                    <Stack spacing={1.5}>
                        {/* Column headers */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '320px 32px 280px', alignItems: 'center' }}>
                            <Typography variant="subtitle2">Field name</Typography>
                            <Box />
                            <Typography variant="subtitle2">Cured Equivalent</Typography>
                        </Box>

                        {/* Editable tag rows */}
                        {personalizationTags.map((tag) => (
                            <Box
                                key={tag}
                                sx={{ display: 'grid', gridTemplateColumns: '320px 32px 280px', alignItems: 'center' }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {tag}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                    →
                                </Typography>
                                <Autocomplete
                                    sx={{ width: 280 }}
                                    options={mergeTagColumns}
                                    getOptionLabel={(opt) => opt.columnName}
                                    value={mergeTagColumns.find((c) => c.columnName === tagMapping[tag]) ?? null}
                                    onChange={(_, newValue) => {
                                        setTagMapping((prev) => ({ ...prev, [tag]: newValue?.columnName ?? '' }));
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} size="small" placeholder="Select field..." />
                                    )}
                                />
                            </Box>
                        ))}
                    </Stack>
                )}

                {/* Auto-mapped system tags — always shown when present, regardless of personalization tags */}
                {systemTagsMapped.map((t) => (
                    <Box
                        key={t}
                        sx={{ display: 'grid', gridTemplateColumns: '320px 32px 280px', alignItems: 'center' }}
                        data-testid="system-tags-mapped-banner"
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'monospace',
                                color: 'text.disabled',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {t}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                            →
                        </Typography>
                        <Typography variant="body2" color="success.main">
                            Unsubscribe link (auto-mapped)
                        </Typography>
                    </Box>
                ))}

                {/* Removed system tags — always shown when present */}
                {systemTagsRemoved.map((t) => (
                    <Box
                        key={t}
                        sx={{ display: 'grid', gridTemplateColumns: '320px 32px 280px', alignItems: 'center' }}
                        data-testid="system-tags-removed-banner"
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'monospace',
                                color: 'text.disabled',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {t}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                            →
                        </Typography>
                        <Typography variant="body2" color="warning.main">
                            Removed — no Cured equivalent
                        </Typography>
                    </Box>
                ))}

                {/* Helper text — shown whenever there are user-mappable tags */}
                {personalizationTags.length > 0 && (
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                            Don&rsquo;t see a field you&rsquo;re expecting? Contact your Cured representative to ensure
                            it&rsquo;s marked for personalization in your data model.
                        </Typography>
                    </Stack>
                )}
            </Stack>
        );
    };

    // ─── Render: Salesforce step 2 — condition mapping ────────────────────────

    const renderConditionExpressionCard = (expr: ConditionExpressionItem, grouped = false) => {
        const mapped = conditionMapping[expr.id];
        const suggested =
            SHOW_SUGGESTED_CONDITIONS && !mapped
                ? getSuggestedCondition(expr, existingConditions, conditionFieldMapping)
                : null;

        const inner = (
            <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                        variant="caption"
                        sx={{
                            px: 0.75,
                            py: 0.25,
                            border: '1px solid',
                            borderColor: 'text.disabled',
                            borderRadius: 0.5,
                            color: 'text.secondary',
                            fontWeight: 600,
                            flexShrink: 0,
                            lineHeight: 1.6,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {expr.isElse ? 'ELSE' : expr.precedingExpressions?.length > 0 ? 'ELSE IF' : 'IF'}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                        {expr.humanReadable}
                    </Typography>
                    {mapped && <CheckCircleOutlineIcon color="success" fontSize="small" sx={{ flexShrink: 0 }} />}
                </Stack>

                {mapped ? (
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" color="success.main">
                            Mapped to: <strong>{mapped.label}</strong> ({mapped.category})
                        </Typography>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                                setConditionMapping((prev) => {
                                    const next = { ...prev };
                                    delete next[expr.id];
                                    return next;
                                });
                            }}
                        >
                            Remap
                        </Button>
                    </Stack>
                ) : (
                    <>
                        {expr.isElse && (
                            <Alert severity="warning" sx={{ py: 0.5 }}>
                                This ELSE block must be mapped to a condition — typically the negation of the preceding
                                IF (e.g. if IF is &ldquo;market = COASTAL&rdquo;, create &ldquo;market ≠
                                COASTAL&rdquo;). Without a condition, both blocks will render simultaneously.
                            </Alert>
                        )}
                        {SHOW_SUGGESTED_CONDITIONS && suggested && (
                            <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'info.main' }}>
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                    <Stack spacing={0.25}>
                                        <Typography variant="caption" color="info.main" fontWeight={600}>
                                            Suggested match
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>{suggested.label}</strong>
                                        </Typography>
                                        {suggested.description && (
                                            <Typography variant="caption" color="text.secondary">
                                                {suggested.description}
                                            </Typography>
                                        )}
                                    </Stack>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                            setConditionMapping((prev) => ({
                                                ...prev,
                                                [expr.id]: {
                                                    expressionId: expr.id,
                                                    conditionId: suggested.id,
                                                    label: suggested.label,
                                                    category: suggested.type,
                                                },
                                            }))
                                        }
                                    >
                                        Use this
                                    </Button>
                                </Stack>
                            </Paper>
                        )}
                        <Autocomplete
                            options={existingConditions}
                            getOptionLabel={(opt) => opt.label}
                            renderOption={(props, opt) => (
                                <li {...props}>
                                    <Stack>
                                        <Typography variant="body2">{opt.label}</Typography>
                                        {opt.description && (
                                            <Typography variant="caption" color="text.secondary">
                                                {opt.description}
                                            </Typography>
                                        )}
                                    </Stack>
                                </li>
                            )}
                            onChange={(_, selected) => {
                                if (!selected) return;
                                setConditionMapping((prev) => ({
                                    ...prev,
                                    [expr.id]: {
                                        expressionId: expr.id,
                                        conditionId: selected.id,
                                        label: selected.label,
                                        category: selected.type,
                                    },
                                }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select existing condition"
                                    size="small"
                                    placeholder="Search by name..."
                                />
                            )}
                            sx={{ maxWidth: 400 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            or create a new one
                        </Typography>
                        <Box>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setConditionDialogExprId(expr.id)}
                                data-testid={`create-condition-button-${expr.id}`}
                            >
                                Create new condition
                            </Button>
                        </Box>
                    </>
                )}
            </Stack>
        );

        if (grouped) return inner;

        return (
            <Paper key={expr.id} variant="outlined" sx={{ p: 2 }}>
                {inner}
            </Paper>
        );
    };

    const renderStep3 = () => {
        // Group IF / ELSEIF / ELSE expressions into their shared chains.
        // precedingExpressions contains raw expression strings (not IDs) for ELSEIF and ELSE.
        // Any expression with precedingExpressions.length > 0 or isElse belongs to the last open group.
        const groups: ConditionExpressionItem[][] = [];
        for (const expr of conditionExpressions) {
            if ((expr.precedingExpressions?.length > 0 || expr.isElse) && groups.length > 0) {
                groups[groups.length - 1].push(expr);
            } else {
                groups.push([expr]);
            }
        }

        return (
            <Stack spacing={3}>
                {conditionExpressions.length === 0 ? (
                    <Alert severity="info" data-testid="no-conditions-banner">
                        No conditional content found in this template. This template doesn&rsquo;t use IF/ELSE display
                        logic — no mapping needed. Click <strong>Next</strong> to continue.
                    </Alert>
                ) : (
                    <>
                        <Typography variant="body1" color="text.secondary">
                            For each condition expression, select an existing display condition or create a new one. All
                            expressions — including ELSE blocks — must be mapped so only one block renders at a time.
                        </Typography>
                        <Stack spacing={3}>
                            {groups.map((group, groupIndex) =>
                                group.length === 1 ? (
                                    renderConditionExpressionCard(group[0])
                                ) : (
                                    <Box
                                        key={groupIndex}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            p: 2,
                                        }}
                                    >
                                        <Stack spacing={2}>
                                            {group.map((expr) => renderConditionExpressionCard(expr))}
                                        </Stack>
                                    </Box>
                                )
                            )}
                        </Stack>
                    </>
                )}
            </Stack>
        );
    };

    // ─── Render: Salesforce step 4 — review ──────────────────────────────────

    const renderStep5 = () => (
        <Stack spacing={2}>
            <Typography variant="body1" color="text.secondary" style={{ marginBottom: '24px' }}>
                Review your import settings and click <strong>Import</strong> to create the template draft.
            </Typography>

            <Accordion defaultExpanded variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Import Configuration</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={0.5}>
                        <Typography variant="body2">
                            <strong>Source:</strong> {SOURCE_LABELS[source]}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Conversion:</strong>{' '}
                            {conversionMode === 'native' ? 'Convert to native BEE JSON' : 'Import as HTML'}
                        </Typography>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded variant="outlined" disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Template Details</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={0.5}>
                        <Typography variant="body2">
                            <strong>Name:</strong> {templateName}
                        </Typography>
                        {subjectLine && (
                            <Typography variant="body2">
                                <strong>Subject:</strong> {subjectLine}
                            </Typography>
                        )}
                        {preHeader && (
                            <Typography variant="body2">
                                <strong>Pre-header:</strong> {preHeader}
                            </Typography>
                        )}
                    </Stack>
                </AccordionDetails>
            </Accordion>

            {personalizationTags.length > 0 && (
                <Accordion defaultExpanded variant="outlined" disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Personalization Tags ({personalizationTags.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={0.5}>
                            {personalizationTags.map((tag) => (
                                <Typography key={tag} variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    %%{tag}%% → {tagMapping[tag] ? `{{${tagMapping[tag]}}}` : '(unmapped)'}
                                </Typography>
                            ))}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            {conditionExpressions.length > 0 && (
                <Accordion defaultExpanded variant="outlined" disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">Display Conditions ({conditionExpressions.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={1}>
                            {conditionExpressions.map((expr) => {
                                const mapped = conditionMapping[expr.id];
                                const badge = expr.isElse
                                    ? 'ELSE'
                                    : expr.precedingExpressions?.length > 0
                                    ? 'ELSE IF'
                                    : 'IF';
                                return (
                                    <Stack key={expr.id} direction="row" spacing={1} alignItems="center">
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                px: 0.75,
                                                py: 0.25,
                                                border: '1px solid',
                                                borderColor: 'text.disabled',
                                                borderRadius: 0.5,
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                flexShrink: 0,
                                                lineHeight: 1.6,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {badge}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}
                                        >
                                            {expr.humanReadable}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color={mapped ? 'success.main' : 'text.disabled'}
                                            sx={{ flexShrink: 0 }}
                                        >
                                            {mapped ? mapped.label : '(unmapped)'}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            {executeError && <Alert severity="error">{executeError}</Alert>}
        </Stack>
    );

    // ─── Render: Inconnect step 1 — tag mapping ────────────────────────────────

    const renderInconnectTagMapping = () => (
        <Stack spacing={3}>
            <Typography variant="body1" color="text.secondary">
                Map Inconnect personalization tags found across your selected templates to their Cured equivalents.
            </Typography>
            {inconnectPersonalizationTags.length === 0 ? (
                <Alert severity="info">
                    No personalization tags found across your selected templates. Click <strong>Next</strong> to
                    continue.
                </Alert>
            ) : (
                <Stack spacing={2}>
                    <Stack spacing={0.5}>
                        <Typography variant="subtitle2">Personalization Tags</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Don&rsquo;t see a field you&rsquo;re expecting? Contact your Cured representative to ensure
                            it&rsquo;s marked for personalization in your data model.
                        </Typography>
                    </Stack>
                    {inconnectPersonalizationTags.map((tag) => (
                        <Stack key={tag} direction="row" spacing={2} alignItems="center">
                            <Box sx={{ minWidth: 200 }}>
                                <Chip label={`{{${tag}}}`} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                →
                            </Typography>
                            <Autocomplete
                                sx={{ width: 280 }}
                                options={mergeTagColumns}
                                getOptionLabel={(opt) => opt.columnName}
                                value={mergeTagColumns.find((c) => c.columnName === tagMapping[tag]) ?? null}
                                onChange={(_, newValue) => {
                                    setTagMapping((prev) => ({ ...prev, [tag]: newValue?.columnName ?? '' }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Cured field"
                                        size="small"
                                        placeholder="Select field..."
                                    />
                                )}
                            />
                        </Stack>
                    ))}
                </Stack>
            )}
        </Stack>
    );

    // ─── Render: Inconnect step 2 — assessment picker (conditional) ──────────

    const renderInconnectAssessmentPicker = () => {
        const templatesWithTag = inconnectEnrichedTemplates.filter(templateHasAssessmentTag);

        if (isAssessmentsLoading) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                    <CircularProgress size={20} role="progressbar" aria-label="Loading assessments" />
                    <Typography variant="body2" color="text.secondary">
                        Loading assessments…
                    </Typography>
                </Box>
            );
        }

        return (
            <Stack spacing={3}>
                <Typography variant="body1" color="text.secondary">
                    {templatesWithTag.length === 1
                        ? 'One of your selected templates contains an'
                        : `${templatesWithTag.length} of your selected templates contain an`}{' '}
                    <strong>{'{{assessment_url}}'}</strong> tag. Select the assessment each template should link to.
                </Typography>
                {templatesWithTag.map((template) => (
                    <Paper key={template.id} variant="outlined" sx={{ p: 3 }}>
                        <Stack spacing={2}>
                            <Typography variant="subtitle2">{template.name}</Typography>
                            <AssessmentDropdown
                                textfieldInput={templateAssessmentSearchInput[template.id] ?? ''}
                                value={templateAssessments[template.id] ?? null}
                                onAssessmentPreview={() => undefined}
                                onChange={(_, option) =>
                                    setTemplateAssessments((prev) => ({ ...prev, [template.id]: option }))
                                }
                                onInputChange={(_, val) =>
                                    setTemplateAssessmentSearchInput((prev) => ({ ...prev, [template.id]: val }))
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={templateConsentRequired[template.id] ?? true}
                                        onChange={(e) =>
                                            setTemplateConsentRequired((prev) => ({
                                                ...prev,
                                                [template.id]: e.target.checked,
                                            }))
                                        }
                                        disabled={!templateAssessments[template.id]}
                                    />
                                }
                                label="Include Consent Form"
                            />
                        </Stack>
                    </Paper>
                ))}
            </Stack>
        );
    };

    // ─── Render: Inconnect step 2 — review ────────────────────────────────────

    const renderInconnectReview = () => {
        // Count supported-language variants with HTML content (1 Cured template per selected Inconnect template)
        const totalTemplates = (inconnectEnrichedTemplates.length > 0
            ? inconnectEnrichedTemplates
            : selectedInconnectTemplates
        ).filter((t) =>
            t.template.some((v) => !!v.message && v.language?.toLowerCase() in INCONNECT_SUPPORTED_LANGUAGES)
        ).length;
        const templatesToReview =
            inconnectEnrichedTemplates.length > 0 ? inconnectEnrichedTemplates : selectedInconnectTemplates;
        return (
            <Stack spacing={3}>
                <Typography variant="body1" color="text.secondary">
                    Review and click <strong>Import</strong> to create {totalTemplates} template draft
                    {totalTemplates !== 1 ? 's' : ''}.
                </Typography>

                {inconnectPersonalizationTags.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Tag Mapping ({inconnectPersonalizationTags.length})
                        </Typography>
                        <Divider sx={{ mb: 1.5 }} />
                        <Stack spacing={0.5}>
                            {inconnectPersonalizationTags.map((tag) => (
                                <Typography key={tag} variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {`{{${tag}}}`} → {tagMapping[tag] ? `{{${tagMapping[tag]}}}` : '(unmapped)'}
                                </Typography>
                            ))}
                        </Stack>
                    </Paper>
                )}

                {hasAssessmentTag && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Assessments
                        </Typography>
                        <Divider sx={{ mb: 1.5 }} />
                        <Stack spacing={1}>
                            {inconnectEnrichedTemplates.filter(templateHasAssessmentTag).map((t) => {
                                const assessment = templateAssessments[t.id];
                                return (
                                    <Stack key={t.id} spacing={0.25}>
                                        <Typography variant="body2" fontWeight={500}>
                                            {t.name}
                                        </Typography>
                                        <Typography variant="body2">
                                            {assessment?.label ?? '(none selected)'}
                                        </Typography>
                                        {assessment && (
                                            <Typography variant="caption" color="text.secondary">
                                                {templateConsentRequired[t.id] ?? true
                                                    ? 'Consent form included'
                                                    : 'No consent form'}
                                            </Typography>
                                        )}
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Paper>
                )}

                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Templates to Import ({totalTemplates})
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Stack spacing={1.5}>
                        {templatesToReview.map((template) => {
                            const supportedVariants = template.template.filter(
                                (v) => !!v.message && v.language?.toLowerCase() in INCONNECT_SUPPORTED_LANGUAGES
                            );
                            if (supportedVariants.length === 0) return null;
                            return (
                                <Stack key={template.id} spacing={0.25}>
                                    <Typography variant="body2" fontWeight={500}>
                                        {template.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {supportedVariants
                                            .map(
                                                (v) =>
                                                    INCONNECT_SUPPORTED_LANGUAGES[v.language.toLowerCase()] ??
                                                    v.language
                                            )
                                            .join(' + ')}
                                        {supportedVariants[0]?.subject ? ` · ${supportedVariants[0].subject}` : ''}
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Paper>

                {executeError && <Alert severity="error">{executeError}</Alert>}
            </Stack>
        );
    };

    // ─── Render: current step dispatcher ─────────────────────────────────────

    const renderCurrentStep = () => {
        if (source === 'inconnect') {
            switch (activeStep) {
                case 0:
                    return renderStep1();
                case 1:
                    return renderInconnectTagMapping();
                case 2:
                    return hasAssessmentTag ? renderInconnectAssessmentPicker() : renderInconnectReview();
                case 3:
                    return renderInconnectReview(); // only reached when assessment step is present
                default:
                    return null;
            }
        }
        switch (activeStep) {
            case 0:
                return renderStep1();
            case 1:
                return renderStep2();
            case 2:
                return renderStep3();
            case 3:
                return renderTemplateDetails();
            case 4:
                return renderStep5();
            default:
                return null;
        }
    };

    // ─── Layout ───────────────────────────────────────────────────────────────

    const lastStepIndex = steps.length - 1;

    return (
        <>
            <ConditionsBuilderDialog
                isDialogVisible={conditionDialogExprId !== null}
                setIsDialogVisible={(visible) => {
                    if (!visible) setConditionDialogExprId(null);
                }}
                onCreateConditionSuccess={(newCondition) => {
                    if (!conditionDialogExprId) return;
                    setConditionMapping((prev) => ({
                        ...prev,
                        [conditionDialogExprId]: {
                            expressionId: conditionDialogExprId,
                            conditionId: newCondition.id,
                            label: newCondition.label,
                            category: newCondition.type,
                        },
                    }));
                    setConditionDialogExprId(null);
                }}
            />
            <Box sx={{ p: 4, width: '100%', maxWidth: 900, mx: 'auto' }}>
                <Box sx={{ mb: 8 }}>
                    <Typography variant="h5" fontWeight={600} mb={4}>
                        Import Template
                    </Typography>
                    <Stepper
                        activeStep={activeStep}
                        connector={
                            <StepConnector
                                sx={{
                                    [`& .${stepConnectorClasses.line}`]: { borderColor: 'divider' },
                                    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
                                        borderColor: 'divider',
                                    },
                                    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
                                        borderColor: 'divider',
                                    },
                                }}
                            />
                        }
                    >
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepLabel
                                    StepIconProps={{
                                        completed: false,
                                        active: index === activeStep,
                                    }}
                                    sx={{ '& .MuiStepIcon-text': { dominantBaseline: 'central' } }}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>
                <Box sx={{ mb: 5 }}>{renderCurrentStep()}</Box>
                <Stack direction="row" spacing={2}>
                    {activeStep > 0 && (
                        <Button variant="outlined" onClick={handleBack} disabled={isExecuting}>
                            Back
                        </Button>
                    )}
                    {activeStep < lastStepIndex ? (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!canProceedFromStep() || isFetchingFullContent}
                        >
                            {isFetchingFullContent ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CircularProgress size={16} color="inherit" />
                                    <span>Loading...</span>
                                </Stack>
                            ) : (
                                'Next'
                            )}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleExecute}
                            disabled={isExecuting || (source !== 'inconnect' && !templateName.trim())}
                        >
                            {isExecuting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : source === 'inconnect' ? (
                                `Import ${selectedInconnectTemplates.length} Template${
                                    selectedInconnectTemplates.length !== 1 ? 's' : ''
                                }`
                            ) : (
                                'Import Template'
                            )}
                        </Button>
                    )}
                    <Button variant="text" onClick={() => history.push(URLPaths.CONTENT)} disabled={isExecuting}>
                        Cancel
                    </Button>
                </Stack>
            </Box>
        </>
    );
};

export default ImportTemplatePage;
